import { useState } from 'react';
import { Button, Popover, PopoverTrigger, PopoverContent } from '@staff-app/ui';
import { NotebookPen } from 'lucide-react';
import { useAddOrderDeviceNote, useOrderDeviceNotes } from '../hooks/useOrderDeviceTasks';

/** Per-order notes — reads/writes staff_audit_log (op:'orderDeviceNote'). */
export function OrderNotes({ invoiceId }: { invoiceId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const { data, loading } = useOrderDeviceNotes(open ? invoiceId : null);
  const [addNote, { loading: saving }] = useAddOrderDeviceNote();
  const notes = data?.orderDeviceNotes ?? [];

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    await addNote({ variables: { invoiceId, text: trimmed } });
    setText('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1" data-testid="order-notes-trigger">
          <NotebookPen className="h-4 w-4" />
          {notes.length > 0 ? notes.length : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex flex-col gap-3">
          <div className="text-sm font-medium">{'Notes'}</div>
          <div className="flex max-h-56 flex-col gap-2 overflow-y-auto">
            {loading ? (
              <div className="text-xs text-muted-foreground">{'Loading…'}</div>
            ) : notes.length === 0 ? (
              <div className="text-xs text-muted-foreground">{'No notes yet.'}</div>
            ) : (
              notes.map((n) => (
                <div key={n._id} className="rounded-md border p-2 text-xs">
                  <div className="whitespace-pre-wrap">{n.text}</div>
                  <div className="mt-1 text-muted-foreground">
                    {n.staffEmail} · {new Date(n.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a note…"
            rows={2}
            className="w-full rounded-md border bg-background p-2 text-sm"
            data-testid="order-note-input"
          />
          <Button size="sm" onClick={submit} disabled={saving || !text.trim()}>
            {saving ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
